/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import { RefreshControl, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useNavigation, type NavigationProp, type ParamListBase } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { SettingsNavigationRow } from '../../../settings/components/SettingsNavigationRow';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { spacing } from '../../../shared/theme/spacing';
import { ensureTicketFonts } from '../../../shared/ticket/ensureTicketFonts'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles';
import { ShellContentFrame } from '../../../shared/layout/ShellContentFrame';
import {
  DEFAULT_MAIN_TAB_ROUTE,
  useLastMainTabOptional,
} from '../../navigation/LastMainTabContext';
import {
  useTabScreenRefreshContext,
} from '../../navigation/TabScreenRefreshContext';
import { useScreenLoad } from '../../../shared/ui/hooks-screen/useScreenLoad';
import { resolveNavItemDescription } from '../../navigation/navItemDescriptions';
import { navigateToManifestItemFromMainTabs } from '../../navigation/navigateFromManifest';
import {
  getMobileMoreSections,
  MOBILE_MORE_TAB_ROUTE,
  type NavigationManifestItem,
} from '../../navigation/navigationManifest';
import { useNavVisibilityContext } from '../../navigation/useNavVisibilityContext';
import { useSupportUnreadCount } from '../../providers/SupportUnreadProvider';
import { SupportUnreadIconAnchor } from '../../support/components/SupportUnreadBadge';
import { withSupportUnreadAccessibilityLabel } from '../../support/components/supportUnreadPresentation';

export function MoreHubScreen() {
  const navigation = useNavigation();
  const { setActiveTabName } = useTabScreenRefreshContext();
  const { refresh: refreshEntitlements } = usePlanEntitlements();

  const ctx = useNavVisibilityContext();

  const sections = useMemo(() => getMobileMoreSections(ctx), [ctx]);
  const lastMainTab = useLastMainTabOptional();
  const { count: supportUnreadCount } = useSupportUnreadCount();
  const supportUnread = supportUnreadCount ?? 0;

  useFocusEffect(
    useCallback(() => {
      setActiveTabName(MOBILE_MORE_TAB_ROUTE);
      ensureTicketFonts();
    }, [setActiveTabName])
  );

  const { refreshing, onRefresh } = useScreenLoad(
    MOBILE_MORE_TAB_ROUTE,
    async (opts) => {
      await refreshEntitlements(opts);
    },
    { refreshOnFocus: false }
  );

  const exitHub = () => {
    (navigation as NavigationProp<ParamListBase>).navigate(
      lastMainTab?.lastTabRoute ?? DEFAULT_MAIN_TAB_ROUTE
    );
  };

  const onPressItem = (item: NavigationManifestItem) => {
    navigateToManifestItemFromMainTabs(navigation as NavigationProp<ParamListBase>, item);
  };

  return (
    <ShellContentFrame>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[ticketColors.gold]}
            tintColor={ticketColors.gold}
          />
        }
      >
        <View style={styles.toolbarRow}>
          <TouchableOpacity
            onPress={exitHub}
            style={styles.closeBtn}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
            accessibilityHint="Volvé a donde estabas"
            testID="auth.more-hub.close"
          >
            <Ionicons name="close" size={18} color={ticketColors.ink3} />
          </TouchableOpacity>
        </View>

        {sections.map((section) => (
          <View key={section.zone} style={styles.section}>
            {section.items[0]?.label === section.label ? null : (
              <Text style={styles.sectionLabel}>{section.label}</Text>
            )}
            <View style={styles.sectionCard}>
              {(() => {
                const nodes: ReactNode[] = [];
                section.items.forEach((item, index) => {
                  const isSupport = item.route === 'SupportReports';
                  nodes.push(
                    <View key={item.id} style={isSupport ? styles.supportRowWrap : undefined}>
                      {isSupport ? (
                        <View style={styles.supportIconSlot}>
                          <SupportUnreadIconAnchor count={supportUnread}>
                            <View style={styles.supportIconWrap}>
                              <Ionicons name={item.icon} size={16} color={ticketColors.ink2} />
                            </View>
                          </SupportUnreadIconAnchor>
                        </View>
                      ) : null}
                      <View style={styles.rowBody}>
                        <SettingsNavigationRow
                          title={item.label}
                          description={
                            resolveNavItemDescription(item.id, ctx.effectiveCapabilities) ??
                            item.zoneLabel
                          }
                          onPress={() => onPressItem(item)}
                          accessibilityLabel={withSupportUnreadAccessibilityLabel(
                            item.label,
                            isSupport ? supportUnread : 0
                          )}
                          iconName={isSupport ? undefined : item.icon}
                          showSeparator={index < section.items.length - 1}
                          testID={`auth.more-hub.${item.id}`}
                        />
                      </View>
                    </View>
                  );
                });
                return nodes;
              })()}
            </View>
          </View>
        ))}
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xl,
    gap: spacing.lg,
  },
  toolbarRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  section: {
    gap: 6,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    paddingLeft: 4,
    paddingBottom: 2,
    fontFamily: ticketFonts.sans,
  },
  sectionCard: {
    ...ticketSurfaces.paperSheet,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  supportRowWrap: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  supportIconSlot: {
    marginTop: spacing.sm,
  },
  supportIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
  },
});
